// src/DoctorShiftsView.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import moment from 'moment';
import 'moment/locale/it';
import { db } from './firebase';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, onSnapshot } from 'firebase/firestore';
import Select from 'react-select';
import { useUser } from './UserContext';
import { toast } from 'react-toastify';

moment.locale('it');

// 🆕 Badge espanso e rifinito per mostrare più informazioni (orario, stanza, note) con una griglia più leggibile
function ShiftBadge({ shift }) {
    const isAssenza = shift.type && shift.type !== 'presence' && !shift.isRoomBased;
    let bg = shift.isRoomBased ? (shift.isTitular ? '#dcfce7' : '#e0f2fe') : '#dbeafe';
    let border = shift.isRoomBased ? (shift.isTitular ? '#86efac' : '#7dd3fc') : '#93c5fd';
    let color = shift.isRoomBased ? (shift.isTitular ? '#166534' : '#0369a1') : '#1e40af';
    let icon = shift.isRoomBased ? (shift.isTitular ? '👑' : '🤝') : '🔓';
    let label = shift.isRoomBased ? `Stanza: ${shift.roomName}` : 'Disponibilità Jolly';

    if (isAssenza) {
        if (shift.type === 'ferie') { bg = '#fffbeb'; border = '#fde68a'; color = '#b45309'; icon = '🌴'; label = 'Ferie'; }
        else if (shift.type === 'malattia') { bg = '#fef2f2'; border = '#fecaca'; color = '#b91c1c'; icon = '🤒'; label = 'Malattia'; }
        else if (shift.type === 'permesso') { bg = '#faf5ff'; border = '#e9d5ff'; color = '#7e22ce'; icon = '⏳'; label = 'Permesso'; }
        else { bg = '#f3f4f6'; border = '#d1d5db'; color = '#4b5563'; icon = '❌'; label = 'Assenza'; }
    }

    return (
        <div style={{
            backgroundColor: bg, border: `1px solid ${border}`, color,
            borderRadius: '6px', padding: '6px 10px', fontSize: '0.75rem', fontWeight: '600',
            marginBottom: '6px', display: 'flex', flexDirection: 'column', gap: '2px',
            boxShadow: '0 1px 2px rgba(0,0,0,0.03)', textAlign: 'left'
        }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontWeight: '900', fontSize: '0.8rem' }}>
                    {icon} {shift.startTime} - {shift.endTime}
                </span>
                <span style={{ fontSize: '0.65rem', opacity: 0.8, textTransform: 'uppercase', fontWeight: '800' }}>
                    {isAssenza ? shift.type : (shift.isRoomBased ? (shift.isTitular ? 'Titolare' : 'Collaboratore') : 'Jolly')}
                </span>
            </div>
            <div style={{ fontSize: '0.7rem', opacity: 0.9, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {label} {shift.notes ? `(${shift.notes})` : ''}
            </div>
        </div>
    );
}

export default function DoctorShiftsView() {
    const { userProfile } = useUser();
    const centerId = userProfile?.centerId;
    const canEdit = userProfile?.permissions?.rooms === 'completo' || userProfile?.permissions?.rooms === 'semi-completo';

    const [currentWeek, setCurrentWeek] = useState(moment().startOf('isoWeek'));
    const [doctors, setDoctors] = useState([]);
    const [rooms, setRooms] = useState([]);
    const [roomAssignments, setRoomAssignments] = useState([]);
    const [doctorShifts, setDoctorShifts] = useState([]);
    const [loading, setLoading] = useState(true);

    const [selectedDoctorFilters, setSelectedDoctorFilters] = useState([]);
    const [cellDetail, setCellDetail] = useState({ open: false, doctorId: null, day: null, entries: [] });
    
    const [focusedDay, setFocusedDay] = useState(null); // 🎯 NUOVO: Stato per il Focus Giornaliero

    const [editingShiftId, setEditingShiftId] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [modalData, setModalData] = useState({
        doctorId: '',
        startTime: '09:00',
        endTime: '18:00',
        type: 'presence',
        dateMode: 'single',
        recurrenceInterval: 1,
        daysOfWeek: [1],
        validFrom: moment().format('YYYY-MM-DD'),
        validUntil: moment().format('YYYY-MM-DD'),
        notes: ''
    });

    useEffect(() => {
        if (!centerId) return;
        const fetchBase = async () => {
            try {
                const [docSnap, roomSnap] = await Promise.all([
                    getDocs(collection(db, 'centers', centerId, 'doctors')),
                    getDocs(collection(db, 'centers', centerId, 'rooms'))
                ]);
                const docList = docSnap.docs.map(d => ({
                    id: d.id,
                    ...d.data(),
                    nomeCompleto: `Dott. ${d.data().lastName || d.data().cognome || ''} ${d.data().firstName || d.data().nome || ''}`.trim(),
                    color: d.data().color || d.data().colore || '#3b82f6'
                })).sort((a, b) => a.nomeCompleto.localeCompare(b.nomeCompleto));

                setDoctors(docList);
                setRooms(roomSnap.docs.map(d => ({ id: d.id, ...d.data(), name: d.data().name || d.data().nome })));
            } catch (err) {
                console.error("Errore caricamento base:", err);
            }
        };
        fetchBase();
    }, [centerId]);

    useEffect(() => {
        if (!centerId) return;
        setLoading(true);

        const unsubRooms = onSnapshot(collection(db, 'centers', centerId, 'roomAssignments'), (snap) => {
            setRoomAssignments(snap.docs.map(d => ({ id: d.id, ...d.data(), source: 'room' })));
        });

        const unsubDocs = onSnapshot(collection(db, 'centers', centerId, 'doctorShifts'), (snap) => {
            setDoctorShifts(snap.docs.map(d => ({ id: d.id, ...d.data(), source: 'doctor_shift' })));
            setLoading(false);
        });

        return () => {
            unsubRooms();
            unsubDocs();
        };
    }, [centerId]);

    const changeWeek = (amount) => {
        setCurrentWeek(prev => prev.clone().add(amount, 'weeks'));
        setFocusedDay(null); // 🎯 FIX: Resetta il filtro se si cambia settimana
    };
    const daysOfWeek = useMemo(() => Array.from({ length: 6 }, (_, i) => currentWeek.clone().add(i, 'days')), [currentWeek]);

    const isShiftActiveOnDay = useCallback((shift, targetDay) => {
        const targetDayStr = targetDay.format('YYYY-MM-DD');
        const isoDay = targetDay.isoWeekday();

        if (shift.validFrom && targetDay.isBefore(moment(shift.validFrom).startOf('day'))) return false;
        if (shift.validUntil && targetDay.isAfter(moment(shift.validUntil).endOf('day'))) return false;

        if ((shift.excludedDates || []).includes(targetDayStr)) return false;

        if (shift.recurrenceMode === 'continuous') return true;

        if (shift.isRecurring) {
            if (!(shift.daysOfWeek || []).includes(isoDay)) return false;
            if ((shift.recurrenceInterval || 1) > 1) {
                const startWeek = moment(shift.validFrom).startOf('isoWeek');
                const diffWeeks = targetDay.clone().startOf('isoWeek').diff(startWeek, 'weeks');
                if (diffWeeks % shift.recurrenceInterval !== 0) return false;
            }
            return true;
        }

        return targetDay.isSame(moment(shift.validFrom), 'day');
    }, []);

    const getDoctorsPresencesForDay = useCallback((day) => {
        const allAssigns = [...roomAssignments, ...doctorShifts];
        const presencesByDoctor = {};

        doctors.forEach(docItem => {
            presencesByDoctor[docItem.id] = [];
        });

        allAssigns.forEach(shift => {
            if (!isShiftActiveOnDay(shift, day)) return;

            const docIds = [shift.doctorId, ...(shift.collaboratorIds || [])].filter(Boolean);

            docIds.forEach(dId => {
                if (!presencesByDoctor[dId]) presencesByDoctor[dId] = [];

                const room = rooms.find(r => r.id === shift.roomId);
                const isTitular = shift.source === 'room' && shift.doctorId === dId;
                const isCollaborator = shift.source === 'room' && (shift.collaboratorIds || []).includes(dId);

                presencesByDoctor[dId].push({
                    ...shift,
                    roomName: room ? room.name : null,
                    isRoomBased: shift.source === 'room',
                    isTitular,
                    isCollaborator
                });
            });
        });

        return presencesByDoctor;
    }, [roomAssignments, doctorShifts, doctors, rooms, isShiftActiveOnDay]);

    const presencesMatrix = useMemo(() => {
        const matrix = {};
        daysOfWeek.forEach(day => {
            matrix[day.format('YYYY-MM-DD')] = getDoctorsPresencesForDay(day);
        });
        return matrix;
    }, [daysOfWeek, getDoctorsPresencesForDay]);

    const openCreateModalFor = (doctorId, day) => {
        setEditingShiftId(null);
        const targetDay = day || moment();
        setModalData({
            doctorId: doctorId || doctors[0]?.id || '',
            startTime: '09:00',
            endTime: '18:00',
            type: 'presence',
            dateMode: 'single',
            recurrenceInterval: 1,
            daysOfWeek: [targetDay.isoWeekday()],
            validFrom: targetDay.format('YYYY-MM-DD'),
            validUntil: targetDay.format('YYYY-MM-DD'),
            notes: ''
        });
        setCellDetail({ open: false, doctorId: null, day: null, entries: [] });
        setModalOpen(true);
    };

    const openEditModal = (shift) => {
        if (shift.isRoomBased) return;
        setEditingShiftId(shift.id);
        const mode = shift.recurrenceMode || (shift.isRecurring ? 'weekly' : 'single');
        setModalData({
            doctorId: shift.doctorId,
            startTime: shift.startTime,
            endTime: shift.endTime,
            type: shift.type || 'presence',
            dateMode: mode,
            recurrenceInterval: shift.recurrenceInterval || 1,
            daysOfWeek: shift.daysOfWeek || [moment(shift.validFrom).isoWeekday()],
            validFrom: shift.validFrom,
            validUntil: shift.validUntil || shift.validFrom,
            notes: shift.notes || ''
        });
        setCellDetail({ open: false, doctorId: null, day: null, entries: [] });
        setModalOpen(true);
    };

    const handleCellClick = (doctorId, day) => {
        if (!canEdit) return;
        const dayStr = day.format('YYYY-MM-DD');
        const entries = presencesMatrix[dayStr]?.[doctorId] || [];
        const editableEntries = entries.filter(s => !s.isRoomBased);

        if (entries.length === 0) {
            openCreateModalFor(doctorId, day);
        } else if (entries.length === 1 && editableEntries.length === 1) {
            openEditModal(editableEntries[0]);
        } else {
            setCellDetail({ open: true, doctorId, day, entries });
        }
    };

    const handleSaveManualShift = async () => {
        if (!modalData.doctorId || !modalData.startTime || !modalData.endTime || !modalData.validFrom) {
            toast.warning("Compila tutti i campi obbligatori.");
            return;
        }
        if (modalData.dateMode !== 'single' && !modalData.validUntil) {
            toast.warning("Indica la data di fine periodo.");
            return;
        }

        try {
            const payload = {
                centerId,
                doctorId: modalData.doctorId,
                startTime: modalData.startTime,
                endTime: modalData.endTime,
                validFrom: modalData.validFrom,
                validUntil: modalData.dateMode === 'single' ? modalData.validFrom : modalData.validUntil,
                isRecurring: modalData.dateMode === 'weekly',
                recurrenceMode: modalData.dateMode,
                recurrenceInterval: modalData.dateMode === 'weekly' ? Number(modalData.recurrenceInterval || 1) : 1,
                daysOfWeek: modalData.dateMode === 'weekly' ? modalData.daysOfWeek : [moment(modalData.validFrom).isoWeekday()],
                excludedDates: [],
                type: modalData.type,
                notes: modalData.notes || '',
                updatedAt: new Date().toISOString()
            };

            if (editingShiftId) {
                await updateDoc(doc(db, 'centers', centerId, 'doctorShifts', editingShiftId), payload);
                toast.success("Registrazione aggiornata!");
            } else {
                await addDoc(collection(db, 'centers', centerId, 'doctorShifts'), { ...payload, createdAt: new Date().toISOString() });
                toast.success("Registrazione salvata con successo!");
            }
            setModalOpen(false);
            setEditingShiftId(null);
        } catch (err) {
            console.error("Errore salvataggio:", err);
            toast.error("Errore nel salvataggio.");
        }
    };

    const handleDeleteDoctorShift = async (shiftId) => {
        if (!window.confirm("Vuoi davvero rimuovere questa registrazione?")) return;
        try {
            await deleteDoc(doc(db, 'centers', centerId, 'doctorShifts', shiftId));
            toast.success("Rimossa con successo.");
            setCellDetail({ open: false, doctorId: null, day: null, entries: [] });
        } catch (err) {
            toast.error("Errore durante l'eliminazione.");
        }
    };

    const doctorOptions = useMemo(() => doctors.map(d => ({ value: d.id, label: d.nomeCompleto })), [doctors]);
    const filteredDoctors = useMemo(
        () => selectedDoctorFilters.length > 0
            ? doctors.filter(d => selectedDoctorFilters.some(f => f.value === d.id))
            : doctors,
        [doctors, selectedDoctorFilters]
    );

    // 🎯 NUOVO: Applica il focus giornaliero ai medici già filtrati
    const displayedDoctors = useMemo(() => {
        if (!focusedDay) return filteredDoctors;
        return filteredDoctors.filter(doc => {
            const entries = presencesMatrix[focusedDay]?.[doc.id] || [];
            return entries.length > 0;
        });
    }, [filteredDoctors, presencesMatrix, focusedDay]);

    const dateModeOptions = [
        { val: 'single', label: '🗓️ Giorno singolo' },
        { val: 'continuous', label: '📅 Periodo continuo' },
        { val: 'weekly', label: '🔁 Ricorrente settimanale' },
    ];

    return (
        <div style={{ padding: '10px 0', display: 'flex', flexDirection: 'column', gap: '15px' }}>
            {/* Header */}
            <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                backgroundColor: 'white', padding: '12px 20px', borderRadius: '12px',
                border: '1px solid #e2e8f0', boxShadow: '0 2px 4px rgba(0,0,0,0.04)', flexWrap: 'wrap', gap: '10px'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <button
                        onClick={() => changeWeek(-1)}
                        style={{ padding: '6px 14px', borderRadius: '6px', border: '1px solid #cbd5e1', background: 'white', cursor: 'pointer', fontWeight: 'bold' }}
                    >
                        ◀ Sett. Prec.
                    </button>
                    <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#1e293b' }}>
                        {currentWeek.format('DD MMMM')} - {currentWeek.clone().add(5, 'days').format('DD MMMM YYYY')}
                    </h3>
                    <button
                        onClick={() => changeWeek(1)}
                        style={{ padding: '6px 14px', borderRadius: '6px', border: '1px solid #cbd5e1', background: 'white', cursor: 'pointer', fontWeight: 'bold' }}
                    >
                        Sett. Succ. ▶
                    </button>
                </div>

                <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
                    <div style={{ width: '280px' }}>
                        <Select
                            options={doctorOptions}
                            value={selectedDoctorFilters}
                            onChange={setSelectedDoctorFilters}
                            placeholder="Filtra per medico/i..."
                            isClearable
                            isMulti
                        />
                    </div>
                    {canEdit && (
                        <button
                            onClick={() => openCreateModalFor(null, moment())}
                            style={{
                                backgroundColor: '#2563eb', color: 'white', border: 'none',
                                padding: '9px 16px', borderRadius: '8px', fontWeight: 'bold',
                                cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap'
                            }}
                        >
                            + Aggiungi Registrazione
                        </button>
                    )}
                </div>
            </div>

            {/* 🎯 GRIGLIA PIÙ MARCATA E SPAZIATA */}
            {loading ? (
                <div style={{ textAlign: 'center', padding: '3rem', color: '#64748b' }}>Caricamento tabellone in corso...</div>
            ) : (
                <div style={{ backgroundColor: 'white', borderRadius: '12px', border: '2px solid #cbd5e1', overflow: 'hidden', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: '960px' }}>
                            <thead>
                                <tr>
                                    <th style={{
                                        padding: '14px 18px', textAlign: 'left', backgroundColor: '#f1f5f9',
                                        borderBottom: '3px solid #cbd5e1', borderRight: '2px solid #cbd5e1',
                                        position: 'sticky', left: 0, zIndex: 2, minWidth: '200px', fontSize: '0.95rem', color: '#0f172a'
                                    }}>
                                        Medico
                                    </th>
                                    {daysOfWeek.map(day => {
                                        const dayStr = day.format('YYYY-MM-DD');
                                        const isFocused = focusedDay === dayStr;
                                        
                                        return (
                                        <th 
                                            key={dayStr} 
                                            onClick={() => setFocusedDay(prev => prev === dayStr ? null : dayStr)}
                                            title={isFocused ? "Rimuovi focus giornaliero" : "Filtra solo medici presenti in questo giorno"}
                                            style={{
                                                padding: '12px 8px', textAlign: 'center', minWidth: '160px',
                                                backgroundColor: isFocused ? '#e0e7ff' : (day.isSame(moment(), 'day') ? '#dbeafe' : '#f8fafc'),
                                                borderBottom: isFocused ? '3px solid #3b82f6' : '3px solid #cbd5e1', 
                                                borderLeft: '2px solid #cbd5e1',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s',
                                                boxShadow: isFocused ? 'inset 0 4px 6px -1px rgba(59, 130, 246, 0.1)' : 'none'
                                            }}
                                            className="hover:bg-slate-100"
                                        >
                                            <div style={{ fontWeight: '900', textTransform: 'capitalize', color: isFocused ? '#1d4ed8' : '#0f172a', fontSize: '0.95rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                                                {day.format('dddd')}
                                                {isFocused && <span style={{ fontSize: '0.8rem' }}>🎯</span>}
                                            </div>
                                            <div style={{ fontSize: '0.8rem', color: isFocused ? '#3b82f6' : '#475569', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', marginTop: '2px' }}>
                                                {day.format('DD/MM')}
                                                {day.isSame(moment(), 'day') && !isFocused && (
                                                    <span style={{ backgroundColor: '#2563eb', color: 'white', fontSize: '0.65rem', padding: '1px 6px', borderRadius: '10px', fontWeight: 'bold' }}>OGGI</span>
                                                )}
                                                {!isFocused && <span style={{ fontSize: '0.65rem', opacity: 0.6, fontStyle: 'italic', fontWeight: 'bold', color: '#94a3b8' }}>🔍 Filtra</span>}
                                            </div>
                                        </th>
                                        );
                                    })}
                                </tr>
                            </thead>
                            <tbody>
                                {displayedDoctors.length === 0 ? (
                                    <tr>
                                        <td colSpan={7} style={{ textAlign: 'center', color: '#94a3b8', padding: '3rem' }}>
                                            <p style={{ fontStyle: 'italic', margin: 0 }}>
                                                {focusedDay ? "Nessun medico presente in questo giorno." : "Nessun medico corrisponde al filtro selezionato."}
                                            </p>
                                            {focusedDay && (
                                                <button 
                                                    onClick={() => setFocusedDay(null)}
                                                    style={{ marginTop: '10px', padding: '6px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', background: 'white', cursor: 'pointer', fontWeight: 'bold', color: '#475569' }}
                                                >
                                                    Rimuovi focus
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ) : (
                                    displayedDoctors.map(docItem => (
                                        <tr key={docItem.id}>
                                            <td style={{
                                                padding: '14px 18px', fontWeight: '800', color: docItem.color,
                                                borderBottom: '2px solid #cbd5e1', borderRight: '2px solid #cbd5e1',
                                                position: 'sticky', left: 0, backgroundColor: 'white', whiteSpace: 'nowrap', zIndex: 1,
                                                fontSize: '0.95rem'
                                            }}>
                                                {docItem.nomeCompleto}
                                            </td>
                                            {daysOfWeek.map(day => {
                                                const dayStr = day.format('YYYY-MM-DD');
                                                const entries = presencesMatrix[dayStr]?.[docItem.id] || [];
                                                // 🎯 Se un giorno ha il focus, opacizziamo leggermente le altre colonne per guidare l'occhio
                                                const isOtherDay = focusedDay && focusedDay !== dayStr;

                                                return (
                                                    <td
                                                        key={dayStr}
                                                        onClick={() => handleCellClick(docItem.id, day)}
                                                        style={{
                                                            padding: '10px', borderBottom: '2px solid #cbd5e1', borderLeft: '2px solid #cbd5e1',
                                                            verticalAlign: 'top', minHeight: '75px', height: 'auto',
                                                            cursor: canEdit ? 'pointer' : 'default',
                                                            transition: 'background-color 0.15s',
                                                            backgroundColor: entries.length > 0 ? '#fafafa' : 'white',
                                                            opacity: isOtherDay ? 0.3 : 1
                                                        }}
                                                        onMouseEnter={e => { if (canEdit && !isOtherDay) e.currentTarget.style.backgroundColor = '#eff6ff'; }}
                                                        onMouseLeave={e => { if (!isOtherDay) e.currentTarget.style.backgroundColor = entries.length > 0 ? '#fafafa' : 'white'; }}
                                                    >
                                                        {entries.length === 0 ? (
                                                            (canEdit && !isOtherDay) && (
                                                                <div style={{ textAlign: 'center', color: '#e2e8f0', fontSize: '1.2rem', fontWeight: 'bold', paddingTop: '10px' }}>+</div>
                                                            )
                                                        ) : (
                                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                                {entries.map((s, idx) => <ShiftBadge key={idx} shift={s} />)}
                                                            </div>
                                                        )}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Modale dettaglio cella */}
            {cellDetail.open && (
                <div style={{
                    position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
                    display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 10000
                }}>
                    <div style={{ backgroundColor: 'white', padding: '22px', borderRadius: '14px', width: '90%', maxWidth: '440px', boxShadow: '0 20px 25px rgba(0,0,0,0.2)' }}>
                        <h4 style={{ margin: '0 0 2px', color: '#1e293b' }}>
                            {doctors.find(d => d.id === cellDetail.doctorId)?.nomeCompleto}
                        </h4>
                        <p style={{ margin: '0 0 16px', fontSize: '0.85rem', color: '#64748b', textTransform: 'capitalize' }}>
                            {cellDetail.day?.format('dddd DD MMMM YYYY')}
                        </p>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '18px' }}>
                            {cellDetail.entries.map((s, idx) => {
                                const isAssenza = s.type && s.type !== 'presence' && !s.isRoomBased;
                                let label = s.isRoomBased 
                                    ? (s.isTitular ? `👑 Titolare - Stanza: ${s.roomName}` : `🤝 Collaboratore - Stanza: ${s.roomName}`) 
                                    : '🔓 Disponibilità Jolly (Stanze Libere)';
                                if (isAssenza) {
                                    if (s.type === 'ferie') label = '🌴 Ferie';
                                    else if (s.type === 'malattia') label = '🤒 Malattia';
                                    else if (s.type === 'permesso') label = '⏳ Permesso';
                                    else label = '❌ Assenza';
                                }
                                return (
                                    <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                                        <div>
                                            <div style={{ fontWeight: 'bold', fontSize: '0.85rem', color: '#1e293b' }}>{s.startTime} - {s.endTime}</div>
                                            <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{label}{s.notes ? ` (${s.notes})` : ''}</div>
                                        </div>
                                        {s.isRoomBased ? (
                                            <span style={{ fontSize: '0.68rem', color: '#94a3b8', fontStyle: 'italic', whiteSpace: 'nowrap' }}>Da Gestione Stanze</span>
                                        ) : canEdit && (
                                            <div style={{ display: 'flex', gap: '6px' }}>
                                                <button onClick={() => openEditModal(s)} title="Modifica" style={{ background: '#eff6ff', border: '1px solid #bfdbfe', color: '#2563eb', borderRadius: '6px', padding: '5px 8px', cursor: 'pointer' }}>✏️</button>
                                                <button onClick={() => handleDeleteDoctorShift(s.id)} title="Elimina" style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', borderRadius: '6px', padding: '5px 8px', cursor: 'pointer' }}>🗑️</button>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        {canEdit && (
                            <button
                                onClick={() => openCreateModalFor(cellDetail.doctorId, cellDetail.day)}
                                style={{ width: '100%', padding: '10px', background: '#f0fdf4', border: '1px dashed #16a34a', color: '#166534', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', marginBottom: '10px' }}
                            >
                                + Aggiungi un'altra voce in questo giorno
                            </button>
                        )}
                        <button
                            onClick={() => setCellDetail({ open: false, doctorId: null, day: null, entries: [] })}
                            style={{ width: '100%', padding: '10px', background: 'white', border: '1px solid #cbd5e1', color: '#475569', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}
                        >
                            Chiudi
                        </button>
                    </div>
                </div>
            )}

            {/* Modale Inserimento / Modifica Registrazione */}
            {modalOpen && (
                <div style={{
                    position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
                    display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 10000
                }}>
                    <div style={{ backgroundColor: 'white', padding: '24px', borderRadius: '14px', width: '90%', maxWidth: '500px', boxShadow: '0 20px 25px rgba(0,0,0,0.2)' }}>
                        <h3 style={{ margin: '0 0 15px', color: '#1e293b' }}>
                            {editingShiftId ? 'Modifica Registrazione' : 'Nuova Registrazione Medico'}
                        </h3>

                        {modalData.type === 'presence' ? (
                            <p style={{ fontSize: '0.85rem', color: '#64748b', margin: '0 0 18px' }}>
                                Registra gli orari in cui il medico è presente in clinica per sbloccare la disponibilità nelle <strong>Stanze Libere</strong>.
                            </p>
                        ) : (
                            <p style={{ fontSize: '0.85rem', color: '#991b1b', margin: '0 0 18px', fontWeight: 'bold' }}>
                                Stai registrando un'assenza. Questi orari copriranno e giustificheranno la mancanza del medico in struttura.
                            </p>
                        )}

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            <div>
                                <label style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#475569' }}>Tipo Registrazione *</label>
                                <select
                                    value={modalData.type}
                                    onChange={e => setModalData(prev => ({ ...prev, type: e.target.value }))}
                                    style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e1', backgroundColor: 'white', fontSize: '0.95rem', fontWeight: 'bold', color: modalData.type === 'presence' ? '#166534' : '#991b1b' }}
                                >
                                    <option value="presence">🟢 Presenza in Struttura (Jolly)</option>
                                    <option value="ferie">🌴 Ferie</option>
                                    <option value="malattia">🤒 Malattia</option>
                                    <option value="permesso">⏳ Permesso</option>
                                    <option value="assenza">❌ Assenza Ingiustificata / Altro</option>
                                </select>
                            </div>

                            <div>
                                <label style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#475569' }}>Medico *</label>
                                <Select
                                    options={doctorOptions}
                                    value={doctorOptions.find(o => o.value === modalData.doctorId)}
                                    onChange={(opt) => setModalData(prev => ({ ...prev, doctorId: opt?.value || '' }))}
                                />
                            </div>

                            <div style={{ display: 'flex', gap: '10px' }}>
                                <div style={{ flex: 1 }}>
                                    <label style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#475569' }}>Ora Inizio</label>
                                    <input
                                        type="time"
                                        value={modalData.startTime}
                                        onChange={e => setModalData(prev => ({ ...prev, startTime: e.target.value }))}
                                        style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                                    />
                                </div>
                                <div style={{ flex: 1 }}>
                                    <label style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#475569' }}>Ora Fine</label>
                                    <input
                                        type="time"
                                        value={modalData.endTime}
                                        onChange={e => setModalData(prev => ({ ...prev, endTime: e.target.value }))}
                                        style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                                    />
                                </div>
                            </div>

                            <div>
                                <label style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#475569' }}>Modalità Data *</label>
                                <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                                    {dateModeOptions.map(opt => (
                                        <button
                                            key={opt.val}
                                            type="button"
                                            onClick={() => setModalData(prev => ({ ...prev, dateMode: opt.val }))}
                                            style={{
                                                flex: 1, padding: '8px 4px', borderRadius: '6px',
                                                border: `2px solid ${modalData.dateMode === opt.val ? '#2563eb' : '#e2e8f0'}`,
                                                backgroundColor: modalData.dateMode === opt.val ? '#eff6ff' : 'white',
                                                color: modalData.dateMode === opt.val ? '#1e40af' : '#475569',
                                                fontWeight: 'bold', fontSize: '0.72rem', cursor: 'pointer'
                                            }}
                                        >
                                            {opt.label}
                                        </button>
                                    ))}
                                </div>
                                {modalData.dateMode === 'continuous' && (
                                    <p style={{ fontSize: '0.72rem', color: '#0f766e', margin: '6px 0 0' }}>
                                        Copre ogni giorno del periodo indicato, festivi inclusi — ideale per le ferie continuative.
                                    </p>
                                )}
                            </div>

                            <div style={{ display: 'flex', gap: '10px' }}>
                                <div style={{ flex: 1 }}>
                                    <label style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#475569' }}>
                                        {modalData.dateMode === 'single' ? 'Data' : 'Dal'}
                                    </label>
                                    <input
                                        type="date"
                                        value={modalData.validFrom}
                                        onChange={e => setModalData(prev => ({ ...prev, validFrom: e.target.value }))}
                                        style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                                    />
                                </div>
                                {modalData.dateMode !== 'single' && (
                                    <div style={{ flex: 1 }}>
                                        <label style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#475569' }}>Al (Incluso)</label>
                                        <input
                                            type="date"
                                            value={modalData.validUntil}
                                            onChange={e => setModalData(prev => ({ ...prev, validUntil: e.target.value }))}
                                            style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                                        />
                                    </div>
                                )}
                            </div>

                            {modalData.dateMode === 'weekly' && (
                                <div>
                                    <label style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#475569' }}>Nei giorni di:</label>
                                    <div style={{ display: 'flex', gap: '6px', marginTop: '4px' }}>
                                        {[
                                            { label: 'L', val: 1 }, { label: 'M', val: 2 }, { label: 'M', val: 3 },
                                            { label: 'G', val: 4 }, { label: 'V', val: 5 }, { label: 'S', val: 6 }
                                        ].map(d => {
                                            const active = modalData.daysOfWeek.includes(d.val);
                                            return (
                                                <button
                                                    key={d.val}
                                                    type="button"
                                                    onClick={() => {
                                                        const newDays = active
                                                            ? modalData.daysOfWeek.filter(x => x !== d.val)
                                                            : [...modalData.daysOfWeek, d.val];
                                                        setModalData(prev => ({ ...prev, daysOfWeek: newDays }));
                                                    }}
                                                    style={{
                                                        width: '34px', height: '34px', borderRadius: '6px',
                                                        border: `1px solid ${active ? '#2563eb' : '#cbd5e1'}`,
                                                        backgroundColor: active ? '#2563eb' : 'white',
                                                        color: active ? 'white' : '#475569',
                                                        fontWeight: 'bold', cursor: 'pointer'
                                                    }}
                                                >
                                                    {d.label}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            <div>
                                <label style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#475569' }}>Note (es. motivo permesso)</label>
                                <input
                                    type="text"
                                    value={modalData.notes}
                                    placeholder="Opzionale..."
                                    onChange={e => setModalData(prev => ({ ...prev, notes: e.target.value }))}
                                    style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                                />
                            </div>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
                            <button
                                onClick={() => { setModalOpen(false); setEditingShiftId(null); }}
                                style={{ padding: '10px 18px', background: 'white', border: '1px solid #cbd5e1', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', color: '#475569' }}
                            >
                                Annulla
                            </button>
                            <button
                                onClick={handleSaveManualShift}
                                style={{ padding: '10px 20px', background: '#2563eb', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}
                            >
                                {editingShiftId ? 'Salva Modifiche' : 'Salva Registrazione'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}